import type { Shell, ShellTemplate } from '@/api.ts';

/**
 * The subline under a shell's name in the left rail.
 *
 * The design asks for `host · N fragments · channel`. **Two of those three do not exist.** A
 * `Shell` carries `shellId`, `slug`, `template`, `isDefault` and a config — there is no host on
 * it (hosts resolve to an org, and the console is never told which), and no channel (a channel
 * points at a registry snapshot, not at a shell). Printing either would draw a routing model the
 * platform does not have, on the screen an operator uses to reason about routing.
 *
 * So it reports what is true: the slug the shell is addressed by, and how many mounts it
 * composes. "mounts" rather than "fragments" because a mount is a slot that a fragment of the
 * same id fills — a shell with three mounts and one published fragment composes one thing, and
 * calling that "3 fragments" would be a count of the wrong noun.
 */
export function shellSubline(shell: Shell): string {
  const mounts = shell.config.mounts.length;
  return `${shell.slug || 'no slug'} · ${mounts} ${mounts === 1 ? 'mount' : 'mounts'}`;
}

/**
 * Why this shell's Delete is disabled, or `null` when it is not.
 *
 * The control plane refuses deleting the default with `default-shell`. Saying so before the click
 * rather than after is the difference between a rule and an error.
 */
export function deleteRefusal(shell: Shell): string | null {
  return shell.isDefault
    ? 'The default shell cannot be deleted. Make another shell the default first.'
    : null;
}

/**
 * The templates `PUT /shells/{id}` will accept.
 *
 * Three, not four. `byo` is deliberately absent from the request schema: a BYO shell is the
 * customer's own host application, and it is set through `POST /shells/{id}/byo`, which fetches
 * the origin and checks it actually serves a shell document first. Letting this form declare a
 * shell BYO would produce one with no origin, which the dataplane can only answer with a 500.
 */
export const SELECTABLE_TEMPLATES: readonly ShellTemplate[] = ['static', 'react-ssr', 'angular-ssr'];

/**
 * Why a shell cannot be saved from this form, or `null` when it can.
 *
 * The trap this closes: a `byo` shell renders fine, lists fine, and opens in this editor fine —
 * and then every save is rejected, because `PUT /shells` does not accept `template: 'byo'`. An
 * operator fixing a nav label on the BYO shell would meet a 422 with no idea why, having changed
 * nothing about the template. Better to disable the button and name the route that owns it.
 */
export function saveRefusal(shell: Shell): string | null {
  return shell.template === 'byo'
    ? 'A BYO shell is updated by pointing it at its origin again, not from this form — `PUT /shells` refuses `template: byo` so that no shell can call itself BYO without an origin to fetch.'
    : null;
}
